/**
 * /skill-sheets — the institution's skill-sheet library (M44).
 *
 * Brian, 22 Sep: "pull any of the current skill sheets (the con-ed station
 * checksheets) into Attest". The checksheet forms the program already runs
 * carry their sheets as data; Import reads one of those files (or its sheet
 * list) without changing a word. An import that changes a sheet makes a new
 * version, and a sheet marked in class always points at the version it was
 * marked on. Retire takes a sheet out of the marking list; nothing is deleted.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import { useAuth } from '../auth/useAuth.js';
import ErrorBanner from '../components/ErrorBanner.jsx';

const DAY = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'America/New_York' });

export function SkillSheetsPage() {
  const { profile } = useAuth();
  const canManage = profile?.permissions?.includes('class.manage') ?? false;
  const institutionId = profile?.institution?.id;
  const [rows, setRows] = useState(undefined);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showRetired, setShowRetired] = useState(false);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      setRows(await apiFetch(`/api/v1/skill-sheets/templates?include_inactive=1${institutionId ? `&institution_id=${institutionId}` : ''}`));
    } catch (err) {
      setError(err);
      setRows(null);
    }
  }, [institutionId]);
  useEffect(() => {
    load();
  }, [load]);

  async function importFile(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    setReport(null);
    try {
      const text = await file.text();
      let body;
      if (/\.json$/i.test(file.name)) {
        const parsed = JSON.parse(text);
        body = { sheets: Array.isArray(parsed) ? parsed : parsed.SKILLS ?? parsed.sheets ?? [] };
      } else {
        body = { html: text };
      }
      const result = await apiFetch('/api/v1/skill-sheets/templates/import', {
        method: 'POST',
        body: JSON.stringify({ ...body, institution_id: institutionId, source: file.name }),
      });
      setReport({ ...result, file: file.name });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function setActive(t, isActive) {
    setBusy(true);
    try {
      await apiFetch(`/api/v1/skill-sheets/templates/${t.id}`, { method: 'PATCH', body: JSON.stringify({ is_active: isActive }) });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const list = (rows ?? []).filter((t) => showRetired || t.is_active);

  return (
    <>
      <h1>Skill sheets</h1>
      <ErrorBanner error={error} />
      <p className="muted">
        The station checksheets an instructor marks during a class. Open a running class → the roster → <strong>Skill sheet</strong> next
        to a person; the completed sheet goes on their record and into the closing packet.
      </p>

      {canManage && (
        <section className="panel" aria-labelledby="import-heading">
          <h2 id="import-heading">Import</h2>
          <p className="muted">
            Choose one of the con-ed checksheet forms (the .html file the evaluators use) or a .json list of its sheets. Attest reads the
            sheets out of it without changing a word. A sheet that already exists unchanged is skipped; a changed sheet becomes a new
            version, and anything already marked keeps the version it was marked on.
          </p>
          <label className="btn btn--auto">
            {busy ? 'Reading…' : 'Choose a form file'}
            <input type="file" accept=".html,.htm,.json" onChange={importFile} disabled={busy} style={{ display: 'none' }} />
          </label>
          {report && (
            <p className="callout callout--ok" role="status">
              <strong>{report.file}:</strong>
              <span className="callout__detail">
                {report.added.length} new ({report.added.join(', ') || 'none'}) · {report.updated.length} updated to a new version
                {report.updated.length ? ` (${report.updated.join(', ')})` : ''} · {report.unchanged.length} unchanged
                {report.refused.length ? ` · ${report.refused.length} not read: ${report.refused.map((r) => `${r.code ?? '?'} (${r.reason})`).join('; ')}` : ''}
              </span>
            </p>
          )}
        </section>
      )}

      <section className="panel" aria-labelledby="library-heading">
        <h2 id="library-heading">Library{rows ? ` (${list.length})` : ''}</h2>
        <label className="checkbox">
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
          <span>Show retired sheets</span>
        </label>
        {rows === undefined && <p className="muted">Loading…</p>}
        {rows && list.length === 0 && <p className="muted">No skill sheets yet.{canManage ? ' Import a checksheet form above.' : ''}</p>}
        {list.length > 0 && (
          <div className="schedule__scroll">
            <table className="schedule__table">
              <thead>
                <tr>
                  <th scope="col">Sheet</th>
                  <th scope="col">Levels</th>
                  <th scope="col">Steps</th>
                  <th scope="col">Version</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) => (
                  <tr key={t.id} style={t.is_active ? undefined : { opacity: 0.6 }}>
                    <td>
                      <button type="button" className="btn btn--quiet btn--auto" onClick={() => setOpen(open === t.id ? null : t.id)}>
                        {t.title}
                      </button>
                      <div className="muted">
                        <code>{t.code}</code>
                        {t.ref ? ` · ${t.ref}` : ''}
                        {t.device_blocks ? ` · devices: ${Object.keys(t.device_blocks).join(', ')}` : ''}
                      </div>
                      {open === t.id && (
                        <ol className="muted" style={{ marginTop: 8 }}>
                          {t.steps.map((s) => (
                            <li key={s.n} style={s.header ? { listStyle: 'none', fontWeight: 700 } : undefined}>
                              {s.critical ? '◆ ' : ''}
                              {s.tier ? `[${s.tier}] ` : ''}
                              {s.text}
                            </li>
                          ))}
                        </ol>
                      )}
                    </td>
                    <td>{t.cert_levels?.join(', ') || <span className="muted">—</span>}</td>
                    <td>
                      {t.steps.filter((s) => !s.header).length}
                      <div className="muted">{t.steps.filter((s) => s.critical).length} critical</div>
                    </td>
                    <td>
                      v{t.version}
                      <div className="muted">{DAY.format(new Date(t.imported_at))}</div>
                    </td>
                    <td>
                      {canManage && (
                        <button type="button" className="btn btn--quiet btn--auto" disabled={busy} onClick={() => setActive(t, !t.is_active)}>
                          {t.is_active ? 'Retire' : 'Bring back'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

export default SkillSheetsPage;
